import './product-teaser-carousel.scss';
import Swiper from 'swiper';
import {A11y, Navigation, Pagination} from 'swiper/modules';

/**
 * Class representing a product carousel with accessibility support.
 */
class ProductCarousel {
    /**
     * Create a ProductCarousel instance.
     * @param {HTMLElement} element - The root element of the carousel.
     */
    constructor(element) {
        /**
         * Root carousel element
         * @type {HTMLElement}
         */
        this.$productCarousel = element;

        /**
         * Swiper container element
         * @type {HTMLElement}
         */
        this.$productCarouselSlider = this.$productCarousel.querySelector('[data-product-carousel="slider"]');

        /**
         * Pagination element
         * @type {HTMLElement}
         */
        this.$productCarouselSliderPagination = this.$productCarousel.querySelector('[data-product-carousel="pagination"]');

        /**
         * Previous button element
         * @type {HTMLElement}
         */
        this.$productCarouselSliderPrev = this.$productCarousel.querySelector('[data-product-carousel="prev"]');

        /**
         * Next button element
         * @type {HTMLElement}
         */
        this.$productCarouselSliderNext = this.$productCarousel.querySelector('[data-product-carousel="next"]');
    }

    /**
     * Initialize the carousel and accessibility features.
     */
    initialize() {
        this.initCarouselSlider();
        this.initA11yFocusFeedback();
    }

    /**
     * Initialize Swiper slider for the carousel.
     * Includes breakpoints, navigation, and pagination configuration.
     */
    initCarouselSlider() {
        if (this.$productCarouselSlider) {
            this.$productCarouselSlider = new Swiper(this.$productCarouselSlider, {
                modules: [A11y, Pagination, Navigation],
                autoHeight: false,
                height: 'auto',
                slidesPerView: 'auto',
                slidesPerGroup: 1,
                speed: 500,
                navigation: {
                    nextEl: this.$productCarouselSliderNext,
                    prevEl: this.$productCarouselSliderPrev
                },
                pagination: {
                    el: this.$productCarouselSliderPagination,
                    clickable: true,
                    dynamicBullets: true,
                    dynamicMainBullets: 1
                },
                breakpoints: {
                    768: {
                        slidesPerGroup: 2,
                        el: this.$productCarouselSliderPagination,
                    },
                    1024: {
                        slidesPerGroup: 3,
                        pagination: {
                            el: this.$productCarouselSliderPagination,
                            dynamicBullets: false
                        }
                    },
                    1440: {
                        slidesPerGroup: 4,
                        pagination: {
                            el: this.$productCarouselSliderPagination,
                            dynamicBullets: false
                        }
                    }
                },
                on: {
                    /**
                     * Adjust pagination on breakpoint change
                     * @param {Swiper} swiper - The Swiper instance
                     */
                    breakpoint: (swiper) => {
                        const el = swiper.pagination?.el;

                        if (!el) {
                            return; // Nothing to update
                        }

                        if (swiper.params.pagination.dynamicBullets) {
                            swiper.pagination.init();
                        } else {
                            setTimeout(() => {
                                el.style.width = '100%';
                                el.classList.remove('swiper-pagination-bullets-dynamic');
                            });
                        }
                    }
                }
            });
        }
    }

    /**
     * Initialize accessibility focus feedback for screen readers.
     * Announces list length, current item, and end of list when tabbing through items.
     */
    initA11yFocusFeedback() {
        const items = this.$productCarousel.querySelectorAll('[data-product-carousel-item]');
        const liveRegion = this.$productCarousel.querySelector('[data-product-carousel-aria-live]');
        const list = this.$productCarousel.querySelector('[data-product-carousel-list]');
        const total = items.length;

        if (!items.length || !liveRegion) return;

        // Get translated strings from data attributes
        const tListIntro = liveRegion.dataset.listIntro;
        const tItemPosition = liveRegion.dataset.itemPosition;
        const tItemEnd = liveRegion.dataset.itemEnd;

        /**
         * Replace placeholders in translation strings
         * @param {string} str - Translation string with placeholders
         * @param {Object} vars - Key-value pairs to replace in string
         * @returns {string} - Interpolated string
         */
        const replaceVars = (str, vars) => {
            let text = str;
            for (const key in vars) {
                text = text.replace(new RegExp('%' + key + '%', 'g'), vars[key]);
            }
            return text;
        };

        // Announce list on first focus
        list.addEventListener('focusin', (e) => {
            if (e.target.matches('[data-product-carousel-item]')) {
                liveRegion.textContent = '';
                setTimeout(() => {
                    liveRegion.textContent = tListIntro;
                }, 50);
            }
        }, {once: true});

        // Announce current item on focus change
        items.forEach((item, index) => {
            item.setAttribute('tabindex', '0'); // ensure focusable
            item.addEventListener('focus', () => {
                const current = index + 1;
                liveRegion.textContent = '';
                setTimeout(() => {
                    if (current === total) {
                        liveRegion.textContent = replaceVars(tItemEnd, {current, total});
                    } else {
                        liveRegion.textContent = replaceVars(tItemPosition, {current, total});
                    }
                }, 50);
            });
        });
    }
}

window.addEventListener('content.loaded', (e) => {
    const eventDetails = e.detail;
    const $context = eventDetails.$context;

    if ($context) {
        const $productTeaserCarousel = $context.querySelectorAll('[data-product-carousel="root"]');

        if ($productTeaserCarousel.length > 0) {
            for (let i = 0; i < $productTeaserCarousel.length; i++) {
                const productTeaserCarousel = new ProductCarousel($productTeaserCarousel[i]);
                productTeaserCarousel.initialize();
            }
        }
    }
});

export {ProductCarousel};
